import { browserDownload } from "@app/lib/utils-browser";
import { GraphQLFrameworkRepository } from "@app/repositories/GraphQLRepositories/FrameworkRepository";
import { GraphQLQuestionnaireRepository } from "@app/repositories/GraphQLRepositories/QuestionnaireRepository";
import {
    type CreateEvsQuestionnaireInput,
    type CreateFrameworkQuestionnaireInput,
} from "@generated/client/graphql";
import {
    skipToken,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { getFrameworks, isSupportedFrameworkType } from "./services";

const frameworkRepository = new GraphQLFrameworkRepository();
const questionnaireRepository = new GraphQLQuestionnaireRepository();

export const frameworkKeys = {
    all: ["frameworks"] as const,
    list: () => [...frameworkKeys.all, "list"] as const,
    frameworksQuestionnaires: () =>
        [...frameworkKeys.all, "questionnaires"] as const,
    frameworksQuestionnairesDataPage: () =>
        [...frameworkKeys.all, "questionnaires", "data-page"] as const,
    all_cms: ["cms", "frameworks"] as const,
    not_initialized: ["frameworks", "not-initialized"] as const,
    frameworkCategory: (cmsGroupId?: string) =>
        [...frameworkKeys.all, "framework-category", cmsGroupId] as const,
    availableFrameworkCategories: () =>
        [...frameworkKeys.all, "available-framework-categories"] as const,
    releasedFrameworkCategories: () =>
        [...frameworkKeys.all, "released-framework-categories"] as const,
    frameworkQuestionTags: (
        cmsGroupId: string | undefined,
        year: number | undefined,
        locale: string | undefined,
    ) =>
        [
            ...frameworkKeys.all,
            "framework-v2",
            cmsGroupId,
            year,
            locale,
        ] as const,
    recentFrameworkProjects: () =>
        [...frameworkKeys.all, "recent-framework-projects"] as const,
    generateCdpWorkplan: (
        thematic: string,
        categories: string[],
        file: string,
    ) =>
        [
            ...frameworkKeys.all,
            "generate-cdp-workplan",
            thematic,
            categories,
            file,
        ] as const,
};

export const useReleasedCmsFrameworks = () => {
    const { data, ...query } = useQuery({
        queryKey: frameworkKeys.all_cms,
        queryFn: frameworkRepository.getManyReleasedFromCms,
    });
    return {
        releasedCmsFrameworks: data?.releasedCmsFrameworks?.filter((f) =>
            isSupportedFrameworkType(f.type),
        ),
        ...query,
    };
};

export const useReleasedFrameworkCategories = () => {
    const { data, ...query } = useQuery({
        queryKey: frameworkKeys.releasedFrameworkCategories(),
        queryFn: frameworkRepository.getReleasedFrameworkCategories,
    });
    return {
        releasedFrameworkCategories: data?.releasedFrameworkCategories,
        ...query,
    };
};

export function useFrameworksPage() {
    const { data, isPending, error } = useQuery({
        queryKey: frameworkKeys.frameworksQuestionnairesDataPage(),
        queryFn: questionnaireRepository.getQuestionnairesDataPage,
    });
    return {
        activeQuestionnaires:
            data?.frameworkQuestionnairesDataPage?.questionnaireDataPages?.filter(
                (f) => f.active,
            ),
        inactiveQuestionnaires:
            data?.frameworkQuestionnairesDataPage?.questionnaireDataPages?.filter(
                (f) => !f.active,
            ),
        isPending,
        error,
    };
}

export function useFrameworks() {
    const { data, isPending, error } = useQuery({
        queryKey: frameworkKeys.list(),
        queryFn: frameworkRepository.getMany,
    });

    const {
        data: frameworkQuestionnaires,
        isPending: isPendingFrameworkQuestionnaires,
        error: errorFrameworkQuestionnaires,
    } = useQuery({
        queryKey: frameworkKeys.frameworksQuestionnaires(),
        queryFn: frameworkRepository.getManyFrameworkQuestionnaires,
    });

    return {
        frameworks: getFrameworks(data?.frameworks),
        frameworkQuestionnaires:
            frameworkQuestionnaires?.frameworkQuestionnaires,
        isPending: isPending || isPendingFrameworkQuestionnaires,
        error: error || errorFrameworkQuestionnaires,
    };
}

export const useCreateFrameworkQuestionnaire = () => {
    const client = useQueryClient();
    return useMutation({
        mutationFn: ({
            frameworkCmsId,
            year,
            tagIds,
            locale,
        }: CreateFrameworkQuestionnaireInput) =>
            questionnaireRepository.createFrameworkQuestionnaire(
                frameworkCmsId,
                year,
                tagIds,
                locale,
            ),
        onSettled: () => {
            client.invalidateQueries({ queryKey: frameworkKeys.all });
        },
    });
};

export const useCreateEvsQuestionnaire = () => {
    const client = useQueryClient();
    return useMutation({
        mutationFn: ({
            year,
            s3File,
            locale,
            entityId,
        }: CreateEvsQuestionnaireInput) =>
            questionnaireRepository.createEvsQuestionnaire(
                year,
                s3File,
                locale,
                entityId,
            ),
        onSettled: () => {
            client.invalidateQueries({ queryKey: frameworkKeys.all });
        },
    });
};

export const useFrameworkCategory = (cmsGroupId?: string) => {
    const { data, isPending, error } = useQuery({
        queryKey: frameworkKeys.frameworkCategory(cmsGroupId),
        queryFn: cmsGroupId
            ? () => frameworkRepository.getFrameworkCategory(cmsGroupId)
            : skipToken,
    });

    return {
        frameworkCategory: data?.frameworkCategory,
        isPending,
        error,
    };
};

export const useAvailableFrameworkCategories = () => {
    const { data: { availableFrameworkCategories } = {}, ...query } = useQuery({
        queryKey: frameworkKeys.availableFrameworkCategories(),
        queryFn: frameworkRepository.getAvailableFrameworkCategories,
    });

    return {
        availableFrameworkCategories,
        ...query,
    };
};

export const useCreateFrameworkProject = () => {
    const client = useQueryClient();
    const { mutate: createFrameworkProject, isPending } = useMutation({
        mutationFn: frameworkRepository.createFrameworkProject,
        onSettled: () => {
            client.invalidateQueries({ queryKey: frameworkKeys.all });
        },
    });
    return {
        createFrameworkProject,
        isPending,
    };
};

export const useFrameworkQuestionTags = (
    cmsGroupId: string | undefined,
    year: number | undefined,
    locale: string | undefined,
) => {
    const { data, isPending, error } = useQuery({
        queryKey: frameworkKeys.frameworkQuestionTags(cmsGroupId, year, locale),
        queryFn:
            !cmsGroupId || !year || !locale
                ? skipToken
                : () =>
                      frameworkRepository.getFrameworkQuestionTags(
                          cmsGroupId,
                          year,
                          locale,
                      ),
    });

    return {
        tags: data?.frameworkTags,
        isPending,
        error,
    };
};

export const useRecentFrameworkProjects = () => {
    const { data, isPending, error } = useQuery({
        queryKey: frameworkKeys.recentFrameworkProjects(),
        queryFn: frameworkRepository.getRecentFrameworkProjects,
    });

    return {
        recentFrameworkProjects: data?.recentFrameworkProjects,
        isPending,
        error,
    };
};

export const useGenerateCdpWorkplan = () => {
    const { mutate: generateCdpWorkplan, ...mutation } = useMutation({
        mutationFn: frameworkRepository.generateCdpWorkplan,
        onSettled: (data) => {
            if (data?.generateCdpWorkplan?.data?.signedUrl) {
                browserDownload(data.generateCdpWorkplan.data.signedUrl);
            }
        },
    });

    return {
        generateCdpWorkplan,
        ...mutation,
    };
};

export const useGenerateEvsWorkplan = () => {
    const { mutate: generateEvsWorkplan, ...mutation } = useMutation({
        mutationFn: frameworkRepository.generateEvsWorkplan,
        onSettled: (data) => {
            if (data?.generateEvsWorkplan?.data?.signedUrl) {
                browserDownload(data.generateEvsWorkplan.data.signedUrl);
            }
        },
    });

    return {
        generateEvsWorkplan,
        ...mutation,
    };
};
